import React, { useState, useEffect } from "react";
import { Form, Container, Button, Row, Col, Badge, Stack, Card, Spinner } from "react-bootstrap";
import { XCircleFill, PlusCircle } from "react-bootstrap-icons";
import { contractApi } from "../../api/contractApi";
import Swal from "sweetalert2";
import "./contracte.css";

const AdaugContract = ({ tema, partizi = [], user }) => {
  const [nrCtr, setNrCtr] = useState("");
  const [ocol, setOcol] = useState("");
  const [tip, setTip] = useState("");
  const [partener, setPartener] = useState("");
  const [proprietar, setProprietar] = useState("Stat");
  const [partiziSelectateIds, setPartiziSelectateIds] = useState([]);
  const [seSalveaza, setSeSalveaza] = useState(false);

  useEffect(() => {
    if (user) {
      const rolul = (user.rol || "").toLowerCase();
      if (rolul !== "administrator" && user.ocol) {
        setOcol(user.ocol);
      }
    }
  }, [user]);

  // Filtrare partizi disponibile pentru ocolul selectat
  const disponibile = partizi.filter((p) => {
    const esteDisponibila = p.stare === "disponibil" && !partiziSelectateIds.includes(p.id);
    if (user?.rol === "administrator") {
      return esteDisponibila && p.ocol === ocol;
    }
    return esteDisponibila && p.ocol === user?.ocol;
  });

  const calcule = partiziSelectateIds.reduce(
    (acc, id) => {
      const p = partizi.find((partida) => partida.id === id);
      if (p) {
        acc.volum += parseFloat(p.volBrut || 0);
        acc.valoare += parseFloat(p.valTotal || 0);
      }
      return acc;
    },
    { volum: 0, valoare: 0 }
  );

  const adaugaPartida = (id) => setPartiziSelectateIds([...partiziSelectateIds, id]);
  const eliminaPartida = (id) => setPartiziSelectateIds(partiziSelectateIds.filter((pid) => pid !== id));

  const tratezSubmit = async (evt) => {
    evt.preventDefault();
    if (!nrCtr || !ocol || partiziSelectateIds.length === 0 || !partener || !proprietar) {
      Swal.fire({
        title: "Atenție!",
        text: "Te rog selectează ocolul, partenerul, proprietarul și cel puțin o partidă!",
        icon: "warning",
        confirmButtonText: "Am înțeles",
        timer: 3000,
        timerProgressBar: true,
        confirmButtonColor: "#f8bb86",
        showClass: { popup: "animate__animated animate__shakeX" },
      });
      return;
    }

    setSeSalveaza(true);
    try {
      await contractApi.create({
        nrCtr,
        ocol,
        tip,
        partener,
        proprietar,
        partiziSelectateIds,
        calcule: {
          volum: parseFloat((calcule?.volum || 0).toFixed(2)),
          valoare: parseFloat((calcule?.valoare || 0).toFixed(2)),
        },
      }, user);

      await Swal.fire({
        title: "Succes!",
        text: `Contractul nr. ${nrCtr} a fost salvat cu succes.`,
        icon: "success",
        showCloseButton: true,
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
        showClass: { popup: "animate__animated animate__fadeInDown" },
      });

      setPartiziSelectateIds([]);
      setNrCtr("");
      setPartener("");
      setTip("");
    } catch (error) {
      console.error("Eroare la submit:", error);
      Swal.fire({
        title: 'Eroare!',
        text: 'Nu s-a putut salva contractul: ' + error.message,
        icon: 'error',
        timer: 3000,
        timerProgressBar: true,
        confirmButtonText: 'Încearcă din nou',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setSeSalveaza(false);
    }
  };

  const isDark = tema === "dark";

  return (
    <Container className="py-4 px-3" style={{ maxWidth: "1200px" }}>
      <div className={`py-4 px-4 contracte-container ${isDark ? "dark-mode" : ""}`}>
        
        {/* HEADER */}
        <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom border-opacity-10 border-secondary">
          <div>
            <h2 className="h4 fw-bold mb-0 tracking-tight text-success">Adaugă Contract</h2>
          </div>
        </div>
        
        <Form onSubmit={tratezSubmit}>
          
          {/* SECȚIUNEA 1: INFORMAȚII GENERALE */}
          <Card className="contracte-card-section">
            <Card.Body className="p-3">
              <Row className="g-3">
                <Col md={2}>
                  <Form.Group>
                    <label className="contracte-label">Nr. ctr.</label>
                    <Form.Control
                      type="text"
                      value={nrCtr}
                      onChange={(e) => setNrCtr(e.target.value)}
                      className="form-control contracte-input focus-ring"
                      placeholder="Ex: 104..."
                    />
                  </Form.Group>
                </Col>
                
                <Col md={2}>
                  <Form.Group>
                    <label className="contracte-label">Tip Contract</label>
                    <Form.Select 
                      value={tip} 
                      onChange={(e) => setTip(e.target.value)} 
                      className="form-select contracte-input focus-ring"
                    >
                      <option value="">Alege Tipul...</option>
                      <option value="Prestari">Prestări servicii</option>
                      <option value="Regie">Regie proprie</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <label className="contracte-label">Ocol Silvic</label>
                    <Form.Select
                      disabled={user?.rol !== "administrator"}
                      value={user?.rol === "administrator" ? ocol : user?.ocol}
                      onChange={(e) => {
                        setOcol(e.target.value);
                        setPartiziSelectateIds([]);
                      }}
                      className="form-select contracte-input focus-ring"
                    >
                      <option value="">Alege Ocolul...</option>
                      {["Ludus", "Targu Mures", "Reghin", "Gurghiu", "Fancel", "Rastolita", "Lunca Bradului", "Sovata", "Tarnaveni", "Sighisoara"].map(o => (
                        <option key={o} value={o}>{o === "Targu Mures" ? "Târgu Mureș" : o}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <label className="contracte-label">Proprietar</label>
                    <Form.Select 
                      value={proprietar} 
                      onChange={(e) => setProprietar(e.target.value)} 
                      className="form-select contracte-input focus-ring"
                    >
                      <option value="">Alege...</option>
                      <option value="Stat">Stat</option>
                      <option value="UAT.">U.A.T.</option>
                      <option value="AD">A.D.</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <label className="contracte-label">Partener / Firmă Exploatare</label>
                    <Form.Control
                      type="text"
                      value={partener}
                      onChange={(e) => setPartener(e.target.value)}
                      className="form-control contracte-input focus-ring"
                      placeholder="Nume firmă..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* SECȚIUNEA 2: SELECȚIE PARTIZI */}
          <Card className="contracte-card-section">
            <Card.Body className="p-3">
              {proprietar !== "Stat" ? (
                <div className="alert alert-warning border-0 shadow-sm rounded-3 d-flex align-items-center gap-2 mb-0">
                  <span>Contractele se operează exclusiv pentru proprietatea publică a statului. Schimbă proprietarul în <strong>„Stat”</strong> pentru a debloca partizile.</span>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="small fw-bold text-uppercase tracking-wider d-block mb-2">
                      Partizi Disponibile:
                    </label>
                    <div className={`p-3 rounded-3 border ${isDark ? "bg-info bg-opacity-5 border-info border-opacity-25" : "bg-info bg-opacity-10 border-info border-opacity-50"}`}>
                      <Stack direction="horizontal" gap={2} className="flex-wrap">
                        {disponibile.map((p) => (
                          <Button
                            key={p.id}
                            variant={isDark ? "outline-info" : "outline-primary"}
                            size="sm"
                            className="rounded-2 py-2 px-3 fw-medium d-flex align-items-center gap-1 shadow-sm transition-all"
                            onClick={() => adaugaPartida(p.id)}
                          >
                            <PlusCircle size={15} /> 
                            <span>{p.fond}</span>
                            <span className="opacity-50">|</span>
                            <span>UP {p.up} / ua {p.ua}</span>
                            <span className="opacity-75 badge bg-dark bg-opacity-20 ms-1">{p.volBrut} mc</span>
                          </Button>
                        ))}
                        {disponibile.length === 0 && (
                          <span className="small italic p-1">Nicio partidă disponibilă pentru criteriile selectate.</span>
                        )}
                      </Stack>
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="small fw-bold text-success text-uppercase tracking-wider d-block mb-2">
                      Partizi Atașate în Prezent la Contract:
                    </label>
                    <div className={`p-3 rounded-3 border ${isDark ? "bg-success bg-opacity-5 border-success border-opacity-25" : "bg-success bg-opacity-10 border-success border-opacity-50"}`}>
                      <Stack direction="horizontal" gap={2} className="flex-wrap">
                        {partiziSelectateIds.map((id) => {
                          const p = partizi.find((part) => part.id === id);
                          return (
                            <Badge
                              key={id}
                              bg="success"
                              className="p-2.5 px-3 rounded-2 d-flex align-items-center gap-3 shadow-xs fw-semibold"
                            >
                              <span>{p?.fond}</span>
                              <span className="vr opacity-50"></span>
                              <span>{p?.valTotal?.toLocaleString("ro-RO")} RON</span>
                              <XCircleFill
                                size={16}
                                className="text-white hover-opacity-100"
                                style={{ cursor: "pointer" }}
                                onClick={() => eliminaPartida(id)}
                              />
                            </Badge>
                          );
                        })}
                        {partiziSelectateIds.length === 0 && (
                          <span className="small italic p-1">Nicio partidă adăugată încă.</span>
                        )}
                      </Stack>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          {/* PANOU REZUMAT FINANCIAR */}
          <div className="contracte-rezumat">
            <Row className="g-4 align-items-center">
              <Col xs={12} md={6} className="text-center text-md-start border-end border-opacity-10 border-secondary">
                <span className="small fw-bold text-uppercase tracking-wider d-block mb-1">Volum Cumulat</span>
                <div className="h2 mb-0 fw-extrabold tracking-tight">
                  {calcule.volum.toFixed(2)} <span className="h5 fw-normal">mc</span>
                </div>
              </Col>
              <Col xs={12} md={6} className="text-center text-md-end">
                <span className="small fw-bold text-success text-uppercase tracking-wider d-block mb-1">Valoare Totală</span>
                <div className="h2 mb-0 text-success fw-extrabold tracking-tight">
                  {calcule.valoare.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="h5 fw-normal">RON</span>
                </div>
              </Col>
            </Row>
          </div>

          {/* SECȚIUNE ACȚIUNE FINALĂ */}
          <div className="pt-2">
            <Button
              variant="success"
              type="submit"
              disabled={seSalveaza}
              className="w-100 py-3 fw-bold text-uppercase tracking-wide shadow rounded-3"
            >
              {seSalveaza ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Se salvează contractul...
                </>
              ) : (
                "Salvează contractul"
              )}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default AdaugContract;